import { Button, Form, Input, Radio, Select, Space } from "antd";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "../../core/components/DatePicker";
import { useDataGrid } from "../../core/components/data-grid/DataGridContext";
import { LocalStorageService } from "../../core/service/local-storage-service";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";
import HospitalSelectBox from "../hospital/HospitalSelectBox";
import HospitalGroupSelectBox from "../hospital-group/HospitalGroupSelectBox";
import ModalitySelectBox from "../modality/ModalitySelectBox";
import InstitutionSelect from "../user/InstitutionSelect";
import UserSelectBox from "../user/UserSelectBox";
import { RE_ASSESSMENT_RESULT } from "../../enums/ReAssessmentEnums";
import { RE_ASSESSMENT_DATE_TYPE } from "./re-assessment-filter-query";
import { buildDefaultReAssessmentFilter } from "./re-assessment-filter-defaults";
import "../../core/style/grid-filter.css";

// Filtre sayfa değişimlerinde korunur (rapor/faturalandırma ekranlarıyla aynı davranış).
// Varsayılan, hızlı filtre çubuğunun "Sıfırla"sıyla ortak (bkz. re-assessment-filter-defaults).
const getDefaultFilter = () => window.reAssessmentFilter || buildDefaultReAssessmentFilter()

/**
 * Tarihler HAM (moment) gönderilir; UTC/yerel dönüşümü tek yerde, re-assessment-filter-query
 * içinde yapılır — grid, sayaç ve istatistik aynı aralığı sorgulasın.
 */
export default function ReAssessmentFilter() {
    const { t } = useTranslation();
    const { hasPermission } = useUser();
    const { onFilterPageSort, queryModel } = useDataGrid();

    const defaultFilter = useRef(getDefaultFilter());
    const formRef = useRef();

    const [dateType, setDateType] = useState(defaultFilter.current.dateType ?? RE_ASSESSMENT_DATE_TYPE.REQUEST);
    const [dates, setDates] = useState({
        dateBegin: defaultFilter.current.dateBegin,
        dateEnd: defaultFilter.current.dateEnd,
    });

    const toFilter = (form, currentDates, currentDateType) => {
        const filter = {
            ...form,
            dateType: currentDateType,
            dateBegin: currentDates.dateBegin,
            dateEnd: currentDates.dateEnd,
        };

        window.reAssessmentFilter = filter;

        return filter;
    }

    const apply = (form) => {
        onFilterPageSort({ filter: toFilter(form, dates, dateType) });
    }

    const reset = () => {
        window.reAssessmentFilter = null;
        window.location.reload();
    }

    // Formu UYGULANAN filtreyle eşitler. Filtre yalnız bu panelden değil grid üstündeki hızlı filtre çubuğundan
    // da uygulanıyor (bkz. ReAssessmentQuickFilterBar); eşitlenmezse paneldeki bir sonraki "Ara" oradan yapılan
    // seçimi sessizce geri alır.
    const syncFormFromFilter = (saved) => {
        if (!saved || !formRef.current) return;

        // Tarih türü ve aralık form alanı değil panelin kendi durumu — forma yazılmaz.
        const { dateType: savedDateType, dateBegin, dateEnd, ...fields } = saved;

        // Uygulanan filtrede OLMAYAN alan formda da kalmamalı (ör. çubuktan "Sıfırla"): önce hepsini boşalt.
        const cleared = Object.fromEntries(
            Object.keys(formRef.current.getFieldsValue(true)).map(k => [k, undefined])
        );

        formRef.current.setFieldsValue({ ...cleared, ...fields });

        setDateType(savedDateType ?? RE_ASSESSMENT_DATE_TYPE.REQUEST);
        setDates({ dateBegin, dateEnd });
    };

    useEffect(() => {
        syncFormFromFilter(window.reAssessmentFilter);

        const filter = toFilter(formRef.current?.getFieldsValue(true) || {}, dates, dateType);
        onFilterPageSort({ filter });
    }, []);

    // Filtre nesnesi yalnız bir filtre UYGULANDIĞINDA yenilenir (sayfa/sıralama/tazeleme aynı nesneyi korur);
    // her uygulamada formu eşitle — kaynağı bu panel ya da hızlı filtre çubuğu.
    const appliedFilter = queryModel?.filter;
    useEffect(() => {
        if (!appliedFilter || Object.keys(appliedFilter).length === 0) return;

        syncFormFromFilter(appliedFilter);
    }, [appliedFilter]);

    return (
        <Form
            ref={formRef}
            className={'grid-filter-form'}
            layout={'vertical'}
            onFinish={apply}
            initialValues={{
                hospitalIdList: defaultFilter.current.hospitalIdList || [],
                modalities: [],
            }}
        >
            <Space style={{ marginBottom: 16 }}>
                <Button type={'primary'} size={'small'} htmlType="submit">{t('Search')}</Button>
                <Button size={'small'} onClick={reset}>{t('Reset')}</Button>
            </Space>

            <Form.Item
                label={(
                    <Radio.Group
                        size={'small'}
                        options={[
                            { label: t('Requested At'), value: RE_ASSESSMENT_DATE_TYPE.REQUEST },
                            { label: t('Resolved At'), value: RE_ASSESSMENT_DATE_TYPE.RESOLVE },
                            { label: t('Study Date'), value: RE_ASSESSMENT_DATE_TYPE.STUDY },
                        ]}
                        onChange={(e) => setDateType(e.target.value)}
                        value={dateType}
                    />
                )}
                style={{ margin: 0, padding: 0 }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <DatePicker
                        locale={LocalStorageService.getAntLocale()}
                        value={dates.dateBegin}
                        showToday={false}
                        onChange={(date) => setDates({ ...dates, dateBegin: date })}
                    />
                    <DatePicker
                        locale={LocalStorageService.getAntLocale()}
                        value={dates.dateEnd}
                        showToday={false}
                        onChange={(date) => setDates({ ...dates, dateEnd: date })}
                    />
                </div>
            </Form.Item>

            <Form.Item label={t('Re-Assessment Result')} name={'outcome'}>
                <Select allowClear placeholder={t('All')}>
                    <Select.Option value={RE_ASSESSMENT_RESULT.CHANGED}>{t('Report Changed')}</Select.Option>
                    <Select.Option value={RE_ASSESSMENT_RESULT.UNCHANGED}>{t('Report Unchanged')}</Select.Option>
                    <Select.Option value={RE_ASSESSMENT_RESULT.OPEN}>{t('Open')}</Select.Option>
                </Select>
            </Form.Item>

            <Form.Item label={t('Original Read User')} name={'originalReadUserId'}>
                <UserSelectBox type={'read'} />
            </Form.Item>

            <Form.Item label={t('Assigned Doctor')} name={'assignedUserId'}>
                <UserSelectBox type={'read'} />
            </Form.Item>

            <Form.Item label={t('Patient Name')} name={'patientName'}>
                <Input allowClear />
            </Form.Item>

            <Form.Item label={t('Patient Id')} name={'patientId'}>
                <Input allowClear />
            </Form.Item>

            <Form.Item label={t('Accession Number')} name={'accessionNumber'}>
                <Input allowClear />
            </Form.Item>

            <Form.Item label={t('Hospital Groups')} name={'hospitalGroupIdList'}>
                <HospitalGroupSelectBox mode={'multiple'} allowClear />
            </Form.Item>

            <Form.Item label={t('Hospital')} name={'hospitalIdList'}>
                <HospitalSelectBox mode={'multiple'} allowClear />
            </Form.Item>

            <Form.Item label={t('Modality')} name={'modalities'}>
                <ModalitySelectBox mode={'multiple'} allowClear />
            </Form.Item>

            {/* InstitutionSelect'in prop'u formItem (formItemProps değil): yanlış adla seçim kutusu forma bağlı değildi,
                panelden kurum filtresi hiç uygulanmıyor ve çubuktan uygulanan kurum paneldeki "Ara" ile düşüyordu. */}
            {hasPermission(PERMISSIONS.VIEW_STUDY_INSTITUTION) && (
                <InstitutionSelect formItem={{ label: t('Institution'), name: 'institutions' }} />
            )}
        </Form>
    );
}
